
import { X, ArrowRight, Heart } from 'lucide-react';

const ProblemSection = () => {
  const problems = [
    {
      traditional: '"Build lasting habits"',
      issue: 'Pressure to be perfect forever makes you feel like a failure',
      emoji: '😔',
      pain: 'Broken streaks = shame spirals'
    },
    {
      traditional: '"Set big goals"',
      issue: 'Overwhelming commitments that feel impossible to maintain',
      emoji: '😰',
      pain: 'All-or-nothing thinking'
    },
    {
      traditional: '"Track everything"',
      issue: 'Complex systems you abandon after two weeks',
      emoji: '🤯',
      pain: 'Too many metrics, too much guilt'
    }
  ];

  return (
    <section className="container max-w-6xl mx-auto px-4 py-24">
      <div className="text-center mb-16">
        <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight">
          Tired of apps that make you
          <span className="block text-red-400">feel like a failure?</span>
        </h2>
        <p className="text-xl text-white/90 max-w-3xl mx-auto font-medium leading-relaxed">
          You're not broken. The system is. Traditional habit apps set you up for shame and disappointment.
        </p>
      </div>

      {/* Problems Grid */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        {problems.map((problem, index) => (
          <div key={index} className="bg-red-500/10 backdrop-blur-sm rounded-3xl p-8 border border-red-400/20 text-center group hover:bg-red-500/15 transition-all duration-300">
            <div className="text-5xl mb-4">{problem.emoji}</div>
            <h3 className="text-2xl font-bold text-red-400 mb-4">{problem.traditional}</h3>
            <div className="flex items-center justify-center mb-4">
              <ArrowRight className="w-6 h-6 text-white/60" />
            </div>
            <p className="text-white/90 font-medium leading-relaxed mb-4">{problem.issue}</p>
            <div className="bg-red-500/20 rounded-2xl p-3 border border-red-400/30">
              <p className="text-red-200 font-bold text-sm">{problem.pain}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Emotional Transition */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-4 bg-white/10 rounded-full px-8 py-4 backdrop-blur-sm border border-white/20">
          <X className="w-8 h-8 text-red-400" />
          <span className="text-white font-bold text-lg">vs</span>
          <Heart className="w-8 h-8 text-yellow-400" />
        </div>
      </div>

      {/* Show Up! Solution */}
      <div className="text-center">
        <div className="bg-gradient-to-br from-yellow-400/20 via-yellow-400/15 to-yellow-400/10 backdrop-blur-lg rounded-[3rem] p-12 border border-yellow-400/30 shadow-2xl">
          <h3 className="text-4xl md:text-5xl font-black text-white mb-6">
            Show Up! gets it.
          </h3>
          <p className="text-xl md:text-2xl text-yellow-400 font-bold mb-8 leading-relaxed">
            Life is hard. Habits are difficult. But showing up every day (even imperfectly) is what matters.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                ✨ <span>Experiment, don't perfect</span>
              </h4>
              <p className="text-white/80 font-medium">Time-limited PACTs remove forever pressure</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                🤗 <span>Support, not shame</span>
              </h4>
              <p className="text-white/80 font-medium">Your daily buddy who celebrates showing up</p>
            </div>
            <div className="bg-white/10 rounded-2xl p-6 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                🧠 <span>Learn about yourself</span>
              </h4>
              <p className="text-white/80 font-medium">Discover what actually works for you</p>
            </div>
          </div>

          {/* Emotional Benefits */}
          <div className="mt-8 bg-white/10 rounded-2xl p-6 border border-white/20">
            <h4 className="text-2xl font-bold text-white mb-4">How Show Up! makes you feel:</h4>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div>
                <span className="text-3xl block mb-2">😌</span>
                <p className="text-white/90 font-medium">Relieved (no forever pressure)</p>
              </div>
              <div>
                <span className="text-3xl block mb-2">💪</span>
                <p className="text-white/90 font-medium">Confident (celebrating small wins)</p>
              </div>
              <div>
                <span className="text-3xl block mb-2">🔍</span>
                <p className="text-white/90 font-medium">Curious (what will I discover?)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
